// src/services/rosterService.ts
// ──────────────────────────────────────────────────────────────────────────────
// Roster Service — Phase 2 API write-through
//
// Read strategy:  mock store remains the source of truth for UI rendering
//                 (Neon allocation UUIDs don't map to mock employee/job IDs yet)
// Write strategy: new allocations and deletes are written to both the mock
//                 store AND the Neon API (/api/roster/allocations).
//                 Neon accumulates real allocation history; future Phase reads
//                 will come from Neon once employee/job UUIDs are seeded.
//
// Phase 3 additions over Phase 1:
//   • getRosterAllocationsForWeek() — filtered by date range and shift
//   • saveAllocation()              — writes to store AND Neon API
//   • deleteAllocation()            — removes from store AND Neon API
//   • pushWeek()                    — calls addRosterPush() from Roster.tsx
//   • getEmployeesWithAllocations() — for grid rendering
// ──────────────────────────────────────────────────────────────────────────────

import {
  getRosterAllocations as _getFromStore,
  setRosterAllocations,
  addRosterPush,
  getRosterPushLog,
  type RosterPushEntry,
} from '../pages/Roster'
import {
  MOCK_ALLOCATIONS,
  JOB_ALLOC_COLOUR,
  type DayAllocation,
  type ShiftType,
} from '../utils/mockDataRoster'
import { MOCK_EMPLOYEES }        from '../utils/mockDataExtended'
import { MOCK_EQUIPMENT }        from '../utils/mockDataExtended'
import { MOCK_JOBS }             from '../utils/mockData'
import { MOCK_CONFIGURED_TASKS } from '../utils/mockDataMobile'
import { getActiveRecalls }      from './recallService'
import { api }                   from '../utils/api'
import { getDefectBlockForEmployee } from '../utils/vehicleAssignments'

// Night allocation seeds — mirrors the private const in Roster.tsx
const NIGHT_ALLOC_SEED: DayAllocation[] = [
  { id:'nl001', employeeId:'e3', jobId:'j4', date:'2026-06-14', shiftType:'NIGHT', allocatedById:'u5', allocatedByName:'Mike Walsh', allocatedAt:'2026-06-12T09:00:00Z', timesheetId:null, notes:'Night crew — Signal Loop Works' },
  { id:'nl002', employeeId:'e3', jobId:'j4', date:'2026-06-15', shiftType:'NIGHT', allocatedById:'u5', allocatedByName:'Mike Walsh', allocatedAt:'2026-06-12T09:00:00Z', timesheetId:null, notes:null },
  { id:'nl003', employeeId:'e3', jobId:'j4', date:'2026-06-16', shiftType:'NIGHT', allocatedById:'u5', allocatedByName:'Mike Walsh', allocatedAt:'2026-06-12T09:00:00Z', timesheetId:null, notes:null },
  { id:'nl004', employeeId:'e3', jobId:'j4', date:'2026-06-17', shiftType:'NIGHT', allocatedById:'u5', allocatedByName:'Mike Walsh', allocatedAt:'2026-06-12T09:00:00Z', timesheetId:null, notes:null },
  { id:'nl005', employeeId:'e3', jobId:'j4', date:'2026-06-18', shiftType:'NIGHT', allocatedById:'u5', allocatedByName:'Mike Walsh', allocatedAt:'2026-06-12T09:00:00Z', timesheetId:null, notes:'Last night — rotate to DAY next week' },
  { id:'nl006', employeeId:'e4', jobId:'j2', date:'2026-06-14', shiftType:'NIGHT', allocatedById:'u4', allocatedByName:'Sarah Lee',  allocatedAt:'2026-06-12T09:10:00Z', timesheetId:null, notes:'Night TMR works — lane closures' },
  { id:'nl007', employeeId:'e4', jobId:'j2', date:'2026-06-15', shiftType:'NIGHT', allocatedById:'u4', allocatedByName:'Sarah Lee',  allocatedAt:'2026-06-12T09:10:00Z', timesheetId:null, notes:null },
  { id:'nl008', employeeId:'e4', jobId:'j2', date:'2026-06-16', shiftType:'NIGHT', allocatedById:'u4', allocatedByName:'Sarah Lee',  allocatedAt:'2026-06-12T09:10:00Z', timesheetId:null, notes:null },
  { id:'nl009', employeeId:'e4', jobId:'j2', date:'2026-06-17', shiftType:'NIGHT', allocatedById:'u4', allocatedByName:'Sarah Lee',  allocatedAt:'2026-06-12T09:10:00Z', timesheetId:null, notes:null },
  { id:'nl010', employeeId:'e4', jobId:'j2', date:'2026-06-18', shiftType:'NIGHT', allocatedById:'u4', allocatedByName:'Sarah Lee',  allocatedAt:'2026-06-12T09:10:00Z', timesheetId:null, notes:null },
]

// ── Internal: ensure store is seeded ──────────────────────────────────────────
function ensureSeeded(): DayAllocation[] {
  const shared = _getFromStore()
  if (shared.length > 0) return shared
  const seed = [...MOCK_ALLOCATIONS, ...NIGHT_ALLOC_SEED]
  setRosterAllocations(seed)
  return seed
}

let _idSeq = 1000
function nextAllocId(): string {
  return `mc_al_${++_idSeq}`
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export function buildWeekDates(
  offsetWeeks: number,
  numWeeks: number,
  shiftView: 'DAY' | 'NIGHT' | 'BOTH',
): string[] {
  const BASE_SUNDAY = '2026-06-14'
  const BASE_MONDAY = '2026-06-15'
  const anchor = shiftView === 'DAY' ? BASE_MONDAY : BASE_SUNDAY
  const dates: string[] = []
  for (let w = 0; w < numWeeks; w++) {
    const wkStart = datePlusDays(anchor, (offsetWeeks + w) * 7)
    for (let d = 0; d < 7; d++) dates.push(datePlusDays(wkStart, d))
  }
  return dates
}

export function datePlusDays(base: string, n: number): string {
  const d = new Date(base + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

export function getDayOfWeek(date: string): number {
  return new Date(date + 'T00:00:00').getDay()
}

export function isOffDay(date: string, shiftView: 'DAY' | 'NIGHT' | 'BOTH'): boolean {
  const dow = getDayOfWeek(date)
  if (shiftView === 'DAY')   return dow === 0 || dow === 6
  if (shiftView === 'NIGHT') return dow === 5 || dow === 6
  return false
}

export function formatDayHeader(date: string, numDates: number): string {
  const d   = new Date(date + 'T00:00:00')
  const day = d.toLocaleDateString('en-AU', { weekday: 'short' })
  const num = d.getDate()
  if (numDates <= 7)  return `${day}\n${num}`
  if (numDates <= 14) return `${day[0]} ${num}`
  return String(num)
}

export { JOB_ALLOC_COLOUR }

// ── Core CRUD ─────────────────────────────────────────────────────────────────

/** All allocations from the shared store. */
export async function getAllocations(): Promise<DayAllocation[]> {
  return Promise.resolve(ensureSeeded())
}

/** All allocations for a specific date range and shift type. */
export async function getAllocationsForDates(
  dates: string[],
  shiftView: 'DAY' | 'NIGHT' | 'BOTH',
): Promise<DayAllocation[]> {
  const all = ensureSeeded()
  return Promise.resolve(all.filter(a => {
    if (!dates.includes(a.date)) return false
    if (shiftView === 'BOTH') return true
    return a.shiftType === shiftView
  }))
}

export async function getEmployeesWithAllocations(): Promise<typeof MOCK_EMPLOYEES> {
  return Promise.resolve(MOCK_EMPLOYEES as any[])
}

/**
 * Save a new allocation — writes to mock store AND Neon API.
 * Neon write is fire-and-forget: a failure does not block the UI.
 */
export async function saveAllocation(
  alloc: Omit<DayAllocation, 'id' | 'allocatedAt' | 'timesheetId'> & {
    allocatedById: string
    allocatedByName: string
  }
): Promise<DayAllocation & { defectWarning?: string }> {
  const current = ensureSeeded()

  // ─ Defect block check ───────────────────────────────────────────────────────
  // If the employee has a currently assigned asset with an OPEN CRITICAL or
  // MAJOR defect, the allocation is blocked. The caller receives an error
  // with defect details so the UI can display a clear safety warning.
  const defectBlock = getDefectBlockForEmployee(alloc.employeeId, MOCK_EQUIPMENT as any[])
  if (defectBlock) {
    const defectSummary = defectBlock.openDefects
      .map(d => `${d.severity}: ${d.description}`)
      .join('; ')
    throw new Error(
      `DEFECT_BLOCK:${defectBlock.assetName} (${defectBlock.assetNumber}) has open ${defectBlock.openDefects[0].severity.toLowerCase()} defect(s) — ${defectSummary}. Resolve defect before rostering this employee.`
    )
  }
  const newAlloc: DayAllocation = {
    ...alloc,
    id:          nextAllocId(),
    allocatedAt: new Date().toISOString(),
    timesheetId: null,
  }

  // Write to mock store (primary — UI reads from here)
  setRosterAllocations([...current, newAlloc])

  // Write-through to Neon (non-blocking — builds allocation history)
  api.post('/api/roster/allocations', {
    employeeId:     newAlloc.employeeId,
    jobId:          newAlloc.jobId,
    allocationDate: newAlloc.date,
    shift:          newAlloc.shiftType ?? 'DAY',
    status:         'SCHEDULED',
    notes:          newAlloc.notes   ?? null,
    createdById:    newAlloc.allocatedById ?? null,
  }).catch(err =>
    console.warn('[rosterService] Neon write failed (non-blocking):', err)
  )

  return newAlloc
}

/**
 * Remove an allocation — removes from mock store AND Neon API.
 * Neon delete is fire-and-forget for mock IDs (Neon IDs differ).
 */
export async function deleteAllocation(allocationId: string): Promise<boolean> {
  const current = ensureSeeded()
  const next = current.filter(a => a.id !== allocationId)
  if (next.length === current.length) return false

  // Remove from mock store
  setRosterAllocations(next)

  // Attempt Neon delete — only succeeds if allocationId is a Neon UUID
  if (allocationId.startsWith('mc_al_') || allocationId.startsWith('nl') || allocationId.match(/^[a-z]\d/)) {
    // Local mock ID — no Neon record to delete, skip silently
  } else {
    api.delete?.(`/api/roster/allocations/${allocationId}`)
      .catch(err => console.warn('[rosterService] Neon delete failed (non-blocking):', err))
  }

  return true
}

export async function getPushLog() {
  return Promise.resolve(getRosterPushLog())
}

// ── Push to Mobile ────────────────────────────────────────────────────────────

export interface PushWeekInput {
  dates:        string[]
  shiftView:    'DAY' | 'NIGHT' | 'BOTH'
  pushedByName: string
  pushedById:   string
  customNote:   string
}

export async function pushWeek(input: PushWeekInput): Promise<RosterPushEntry[]> {
  const allocs = ensureSeeded()

  const tasksByJob: Record<string, RosterPushEntry['tasks']>     = {}
  const matsByJob:  Record<string, RosterPushEntry['materials']> = {}

  for (const ct of MOCK_CONFIGURED_TASKS as any[]) {
    if (!ct.pushedToMobile) continue
    if (!tasksByJob[ct.jobId]) tasksByJob[ct.jobId] = []
    tasksByJob[ct.jobId].push({
      taskId:        ct.id,
      title:         ct.title,
      specClauseRef: ct.specClauseRef ?? null,
      holdPointRef:  ct.holdPointLinkage ?? null,
      discipline:    ct.discipline,
    })
    if (ct.materialsRequired) {
      if (!matsByJob[ct.jobId]) matsByJob[ct.jobId] = []
      for (const m of ct.materialsRequired) {
        if (!matsByJob[ct.jobId].find((x: any) => x.bomLineId === m.bomLineId)) {
          matsByJob[ct.jobId].push({
            bomLineId:   m.bomLineId,
            description: m.description,
            qty:         m.qty,
            unit:        m.unit,
            stockStatus: m.stockStatus,
          })
        }
      }
    }
  }

  const entries: RosterPushEntry[] = input.dates.flatMap(date =>
    allocs
      .filter(a =>
        a.date === date &&
        (input.shiftView === 'BOTH' || a.shiftType === input.shiftView)
      )
      .map(a => {
        const emp = (MOCK_EMPLOYEES as any[]).find(e => e.id === a.employeeId)
        const job = (MOCK_JOBS      as any[]).find(j => j.id === a.jobId)
        return {
          id:           `push_${a.id}_${Date.now()}`,
          employeeId:   a.employeeId,
          employeeName: emp?.fullName ?? a.employeeId,
          jobId:        a.jobId,
          jobNumber:    job?.jobNumber ?? a.jobId,
          date,
          shiftType:    a.shiftType,
          pushedAt:     new Date().toISOString(),
          pushedByName: input.pushedByName,
          status:       'PENDING' as const,
          confirmedAt:  null,
          tasks:        tasksByJob[a.jobId] ?? [],
          materials:    matsByJob[a.jobId]  ?? [],
          customNote:   input.customNote || null,
        }
      })
  )

  if (entries.length === 0) return []
  addRosterPush(entries)
  return entries
}

export async function previewPush(input: Omit<PushWeekInput, 'pushedById' | 'pushedByName' | 'customNote'>): Promise<Array<{
  employeeName: string
  jobNumber:    string
  date:         string
  shiftType:    string
}>> {
  const allocs = ensureSeeded()
  return Promise.resolve(input.dates.flatMap(date =>
    allocs
      .filter(a =>
        a.date === date &&
        (input.shiftView === 'BOTH' || a.shiftType === input.shiftView)
      )
      .map(a => {
        const emp = (MOCK_EMPLOYEES as any[]).find(e => e.id === a.employeeId)
        const job = (MOCK_JOBS      as any[]).find(j => j.id === a.jobId)
        return {
          employeeName: emp?.fullName ?? a.employeeId,
          jobNumber:    job?.jobNumber ?? a.jobId,
          date,
          shiftType:    a.shiftType,
        }
      })
  ))
}

export async function getRecalledCrewForDates(dates: string[]): Promise<Set<string>> {
  const recalls = await getActiveRecalls()
  const recalledIds = new Set<string>()
  for (const r of recalls) {
    for (const c of r.affectedCrew) {
      if (c.acknowledgedAt && !c.returnedToSiteAt) recalledIds.add(c.employeeId)
    }
  }
  return recalledIds
}
